/**
 * Meta Pixel Service
 * Handles all Meta Pixel tracking events with proper error handling
 */

class MetaPixelService {
  constructor() {
    this.pixelId = process.env.REACT_APP_META_PIXEL_ID;
    this.enabled = true;
    this.isInitialized = false;
    this.isAvailable = typeof window !== 'undefined' && window.fbq;
  }

  /**
   * Initialize Meta Pixel
   * Call this once on app startup
   */
  init() {
    if (!this.enabled) return;
    if (!this.pixelId) {
      console.warn('[Meta Pixel] Pixel ID not configured');
      return;
    }

    if (!this.isAvailable) {
      console.warn('[Meta Pixel] fbq not loaded');
      return;
    }

    try {
      window.fbq('init', this.pixelId);
      this.isInitialized = true;
      console.log('[Meta Pixel] Initialized:', this.pixelId);
    } catch (error) {
      console.error('[Meta Pixel] Initialization error:', error);
    }
  }

  /**
   * Generate unique event ID for deduplication
   * Format: timestamp_randomString
   */
  generateEventId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${random}`;
  }

  /**
   * Track PageView event
   * Called when user lands on Special Offer page
   */
  trackPageView() {
    if (!this.enabled || !this.isAvailable) return;

    try {
      window.fbq('track', 'PageView');
      console.log('[Meta Pixel] PageView tracked');
    } catch (error) {
      console.error('[Meta Pixel] PageView error:', error);
    }
  }

  /**
   * Track Scroll Depth event
   * Called when user reaches scroll milestones (25%, 50%, 75%, 100%)
   *
   * @param {number} percentage - Scroll depth percentage (25, 50, 75, or 100)
   */
  trackScrollDepth(percentage) {
    if (!this.enabled || !this.isAvailable) return;

    try {
      // Track as custom event with percentage parameter
      window.fbq('trackCustom', 'ScrollDepth', {
        scroll_depth: percentage
      });
      console.log(`[Meta Pixel] Tracking scroll depth: ${percentage}%`);
    } catch (error) {
      console.error('[Meta Pixel] Error tracking scroll depth:', error);
    }
  }

  /**
   * Track InitiateCheckout event
   * Called when payment modal opens
   *
   * @param {Object} params - Event parameters
   * @param {number} params.value - Order value in INR
   * @param {string} params.currency - Currency code (INR)
   */
  trackInitiateCheckout(params = {}) {
    if (!this.enabled || !this.isAvailable) return;

    try {
      const eventData = {
        value: params.value || 75,
        currency: params.currency || 'INR',
        content_name: params.contentName || 'Special Offer - One Question'
      };

      const options = params.eventId ? { eventID: params.eventId } : {};
      window.fbq('track', 'InitiateCheckout', eventData, options);
      console.log('[Meta Pixel] InitiateCheckout tracked', eventData);
    } catch (error) {
      console.error('[Meta Pixel] InitiateCheckout error:', error);
    }
  }

  /**
   * Track AddPaymentInfo event
   * Called right before opening Razorpay (order created, user about to pay)
   *
   * @param {Object} params - Event parameters
   * @param {number} params.value - Order value in INR
   * @param {string} params.currency - Currency code (INR)
   * @param {string} params.contentName - Content name for the product
   * @param {string} params.eventId - Unique event ID for deduplication with CAPI
   */
  trackAddPaymentInfo(params = {}) {
    if (!this.enabled || !this.isAvailable) return;

    try {
      const eventData = {
        value: params.value || 2550,
        currency: params.currency || 'INR',
        content_name: params.contentName || 'Astrology Consultation',
        content_type: 'product'
      };

      const options = params.eventId ? { eventID: params.eventId } : {};
      window.fbq('track', 'AddPaymentInfo', eventData, options);
      console.log('[Meta Pixel] AddPaymentInfo tracked', eventData);
    } catch (error) {
      console.error('[Meta Pixel] AddPaymentInfo error:', error);
    }
  }

  /**
   * Track Purchase event
   * Called when payment succeeds
   *
   * @param {Object} params - Event parameters
   * @param {number} params.value - Purchase value in INR
   * @param {string} params.currency - Currency code (INR)
   * @param {string} params.eventId - Unique event ID for deduplication with CAPI
   * @param {string} params.purchaseId - Purchase record ID from backend
   */
  trackPurchase(params = {}) {
    if (!this.enabled || !this.isAvailable) return;

    try {
      const eventData = {
        value: params.value || 75,
        currency: params.currency || 'INR',
        content_name: params.contentName || 'Special Offer - One Question',
        content_type: 'product'
      };

      // Add event_id for deduplication with server-side CAPI
      const options = params.eventId ? { eventID: params.eventId } : {};

      window.fbq('track', 'Purchase', eventData, options);
      console.log('[Meta Pixel] Purchase tracked', { eventData, options });
    } catch (error) {
      console.error('[Meta Pixel] Purchase error:', error);
    }
  }
}

// Export singleton instance
const metaPixelService = new MetaPixelService();
export default metaPixelService;
